import { USER_LOAN_PROGRAM } from '@/config/constants';
import type {
  LoanApplication,
  LoanFlowProfile,
  LoanProductType,
} from '@/types/LoanApplicationType';

const PURCHASE_FLOW_PROFILES = ['purchase', 'investor_dscr'] as const;

export type LoanHubFlowBucket = 'purchase' | 'investor' | 'refinance';

export type LoanHubFlowMeta = {
  bucket: LoanHubFlowBucket;
  label: string;
  chipColor: 'primary' | 'secondary' | 'info';
  summary: string;
  propertyHandling: string;
};

const FLOW_META: Record<LoanHubFlowBucket, Omit<LoanHubFlowMeta, 'bucket'>> = {
  purchase: {
    label: 'Purchase',
    chipColor: 'primary',
    summary:
      'One master file for home shopping. Link each listing in your property dashboard.',
    propertyHandling:
      'Property address, taxes, and insurance are filled per listing in Deal Room → Stage 3—not on this hub page.',
  },
  investor: {
    label: 'Investor / DSCR',
    chipColor: 'secondary',
    summary:
      'Investor purchase or DSCR file. Track rentals and operating figures per property.',
    propertyHandling:
      'Subject property and DSCR figures live with each listing in Deal Room → Stage 3.',
  },
  refinance: {
    label: 'Refinance',
    chipColor: 'info',
    summary:
      'One application per property you are refinancing, started from your refinance workspace.',
    propertyHandling:
      'Property information is completed inside the application (Property section).',
  },
};

export function getLoanHubFlowBucket(
  app: Pick<LoanApplication, 'flowProfile' | 'productType'>
): LoanHubFlowBucket {
  if (app.flowProfile === 'refinance') return 'refinance';
  if (app.flowProfile === 'investor_dscr' || app.productType === 'dscr') {
    return 'investor';
  }
  return 'purchase';
}

export function getLoanHubFlowMeta(
  app: Pick<LoanApplication, 'flowProfile' | 'productType'>
): LoanHubFlowMeta {
  const bucket = getLoanHubFlowBucket(app);
  return { bucket, ...FLOW_META[bucket] };
}

export function formatFlowProfileLabel(
  flow?: LoanFlowProfile | string
): string {
  if (!flow) return '';
  switch (flow) {
    case 'investor_dscr':
      return 'Investor DSCR';
    case 'purchase':
      return 'Purchase';
    case 'refinance':
      return 'Refinance';
    default:
      return flow.replace(/_/g, ' ');
  }
}

export function getLoanApplicationHubTitle(app: LoanApplication): string {
  const focus = app.focusProductId ?? app.sourceProductId;
  const address =
    app.property?.subjectPropertyAddress?.trim() ||
    linkedProductLabel(focus ?? '') ||
    '';

  if (app.flowProfile === 'refinance') {
    return address || 'Refinance property';
  }

  const linkedCount = app.linkedPurchaseProductIds?.length ?? 0;
  if (linkedCount > 1) {
    return `Purchase file · ${linkedCount} linked properties`;
  }
  return address || 'Purchase application';
}

/** Latest refinance application for this user (one master file per backend rules). */
export function pickRefinanceApplication(
  apps: LoanApplication[]
): LoanApplication | undefined {
  const refis = apps.filter((a) => a.flowProfile === 'refinance');
  if (!refis.length) return undefined;
  return [...refis].sort((a, b) =>
    String(b.updatedAt ?? '').localeCompare(String(a.updatedAt ?? ''))
  )[0];
}

export function refinanceProductTypeFromProgram(
  program: USER_LOAN_PROGRAM | string | undefined
): LoanProductType {
  switch (program) {
    case USER_LOAN_PROGRAM.FHA:
      return 'fha';
    case USER_LOAN_PROGRAM.VA:
      return 'va';
    case USER_LOAN_PROGRAM.USDA:
      return 'usda';
    case USER_LOAN_PROGRAM.DSCR:
      return 'dscr';
    default:
      return 'conventional';
  }
}

export function groupApplicationsForHub(
  apps: LoanApplication[]
): Record<LoanHubFlowBucket, LoanApplication[]> {
  const groups: Record<LoanHubFlowBucket, LoanApplication[]> = {
    purchase: [],
    investor: [],
    refinance: [],
  };
  for (const app of apps) {
    groups[getLoanHubFlowBucket(app)].push(app);
  }
  return groups;
}

/** One row per purchase flow profile; all refinance (and other) apps listed as-is. */
export function dedupePurchaseAppsForHub(
  apps: LoanApplication[]
): LoanApplication[] {
  const bestByFlow = new Map<string, LoanApplication>();
  const rest: LoanApplication[] = [];

  for (const a of apps) {
    if (
      a.flowProfile &&
      PURCHASE_FLOW_PROFILES.includes(
        a.flowProfile as (typeof PURCHASE_FLOW_PROFILES)[number]
      )
    ) {
      const k = a.flowProfile;
      const prev = bestByFlow.get(k);
      if (!prev || String(a.updatedAt ?? '') > String(prev.updatedAt ?? '')) {
        bestByFlow.set(k, a);
      }
    } else {
      rest.push(a);
    }
  }

  return [...rest, ...bestByFlow.values()].sort((x, y) =>
    String(y.updatedAt ?? '').localeCompare(String(x.updatedAt ?? ''))
  );
}

export function resolveProductId(
  ref: string | { _id?: string } | null | undefined
): string {
  if (!ref) return '';
  return typeof ref === 'string' ? ref : String(ref._id ?? '');
}

export function linkedProductLabel(
  ref:
    | string
    | {
        address?: { fullAddress?: string; street?: string };
        name?: string;
        productId?: string;
      }
): string {
  if (typeof ref === 'string') return 'Property';
  const a = ref.address;
  return a?.fullAddress || a?.street || ref.name || ref.productId || 'Property';
}
